import { createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';

import { OrderingOperations, OrderingConstants, OrderingSelectors } from 'polygon-ordering';

import debug from '../utils/debug';

import { setDeliveryEstimateInProgress } from '../slices/deliveryEstimateInProgress';
import { setDesiredDeliveryAddressNotFound } from '../slices/desiredDeliveryAddressNotFound';
import { setDeliveryEstimateFailed } from '../slices/deliveryEstimateFailed';
import { setCurrentModal } from './setCurrentModal';
import { MESSAGE_MODAL_ID } from '../modals/MessageModal';
import i18next from 'i18next';
import { DELIVERY_DETAILS_MODAL_ID } from '../modals/DeliveryDetailsModal';
import { RootState } from '../store';
import { adjustOrder } from './adjustOrder';
import { updateLoadingDependencies } from '../slices/loadingDependencies';
import { setInlineAlert } from '../slices/inlineAlert';
import { deliveryAddressSelected } from '../utils/analytics';

const { getLocation, getBypassDeliveryDetails } = OrderingSelectors;
const { updateKeyOrderProperty } = OrderingOperations;
const { FAILURE_REASON, ASAP_TIME, SALE_TYPE } = OrderingConstants;

export const performDeliveryEstimate = createAsyncThunk(
  'performDeliveryEstimate',
  async (
    data: {
      deliveryAddress: DeliveryAddress;
      desiredDeliveryTime: string;
      forceUseClosestStore?: boolean;
      fromAddDelivery?: boolean;
      saleType?: SALE_TYPE;
    },
    { dispatch, rejectWithValue, getState },
  ) => {
    let {
      deliveryAddress,
      desiredDeliveryTime,
      forceUseClosestStore,
      fromAddDelivery,
      saleType = SALE_TYPE.DELIVERY,
    } = data;

    const location = getLocation(getState() as RootState);
    const bypassDeliveryDetails = getBypassDeliveryDetails(getState() as RootState);
    const locationId = bypassDeliveryDetails && !forceUseClosestStore ? location?.id : undefined;

    debug('performing delivery estimate', {
      deliveryAddress,
      desiredDeliveryTime,
      locationId,
      saleType,
    });

    dispatch(setInlineAlert({ visible: false }));

    const showDeliveryDetailsModal = () => {
      dispatch(
        setCurrentModal({
          modalId: DELIVERY_DETAILS_MODAL_ID,
          params: {
            allowRedirect: false,
          },
        }),
      );
    };

    const showDeliveryAddressModal = () => {
      dispatch(
        setCurrentModal({
          modalId: DELIVERY_DETAILS_MODAL_ID,
          params: { saleType: saleType || SALE_TYPE.DELIVERY, allowRedirect: false },
        }),
      );
    };

    try {
      if (
        desiredDeliveryTime &&
        desiredDeliveryTime !== ASAP_TIME &&
        moment() > moment(desiredDeliveryTime)
      ) {
        desiredDeliveryTime = ASAP_TIME;
      }
      dispatch(setDeliveryEstimateFailed(false));
      dispatch(setDeliveryEstimateInProgress(true));

      const combinedAddressString = [
        deliveryAddress.buildingInformation,
        deliveryAddress.formattedAddress,
      ]
        .filter(Boolean)
        .join(', ');

      const dropOffCoords = deliveryAddress.deliveryDropOffCoordinates ?? deliveryAddress.location;

      await dispatch(
        updateKeyOrderProperty({
          autoApply: false,
          deliveryAddress: combinedAddressString,
          deliveryDropOffCoordinates: dropOffCoords,
          saleType,
          desiredDeliveryTime,
          locationId,
          bypassDeliveryDetails,
        }),
      ).unwrap();

      dispatch(setDeliveryEstimateInProgress(false));
      dispatch(setDesiredDeliveryAddressNotFound(false));

      deliveryAddressSelected({ success: true });
    } catch (e: any) {
      const { reason, userReason, systemReason } = e as any;

      if (typeof e.e !== 'string') {
        dispatch(
          setInlineAlert({
            visible: true,
            type: 'info',
            body: (userReason as string) || 'An error occurred while processing your request.',
          }),
        );
      }

      dispatch(setDeliveryEstimateFailed(true));
      deliveryAddressSelected({ success: false });

      let alertButtons: AlertButton[] = [
        {
          text: i18next.t('button.continue'),
          isPreferred: true,
        },
      ];

      if (
        reason === FAILURE_REASON.ADDRESS_NOT_FOUND ||
        String(systemReason).includes('Delivery is currently unavailable for your location')
      ) {
        dispatch(setDesiredDeliveryAddressNotFound(true));
      }

      const asapAndBypassingDeliveryDetails =
        desiredDeliveryTime == ASAP_TIME && bypassDeliveryDetails;

      if (asapAndBypassingDeliveryDetails) {
        alertButtons = [
          {
            text: i18next.t('button.changeAddress'),
            action: showDeliveryAddressModal,
            isPreferred: true,
          },
          {
            text: forceUseClosestStore
              ? i18next.t('button.retryClosestStore')
              : i18next.t('button.useClosestStore'),
            action: async () => {
              try {
                await dispatch(setCurrentModal(null));
                dispatch(updateLoadingDependencies({ performingDeliveryEstimates: true }));

                await dispatch(
                  performDeliveryEstimate({
                    deliveryAddress,
                    desiredDeliveryTime,
                    forceUseClosestStore: true,
                  }),
                ).unwrap();

                await dispatch(
                  adjustOrder({
                    saleType: saleType || SALE_TYPE.DELIVERY,
                    bypassDeliveryDetailsInit: true,
                  }),
                );
              } finally {
                dispatch(updateLoadingDependencies({ performingDeliveryEstimates: false }));
              }
            },
          },
        ];

        dispatch(
          setCurrentModal({
            modalId: MESSAGE_MODAL_ID,
            params: {
              message:
                fromAddDelivery &&
                String(systemReason).includes('Delivery is currently unavailable for your location')
                  ? i18next.t('bypassDeliveryAddressAddAddressFailed')
                  : systemReason,
              alertButtons,
              onClose: showDeliveryDetailsModal,
            },
            clearReturnModalDetails: true,
          }),
        );
      }

      dispatch(setDeliveryEstimateInProgress(false));

      return rejectWithValue(e);
    }
  },
);
